import { ServiceInfoDc } from "@evergis/api";
import React, { FC, useMemo, useState } from "react";
import { ActionsGroup, DialogContent, FlatButton } from "@evergis/ui";
import { useTranslation } from "react-i18next";

import {
  ApplyButton,
  CancelButton,
  DialogActions as DialogActionsContainer,
  RestrictedWidthContainer,
} from "components/Dialogs/DialogComponents/styled";

import { Step0 } from "./Step0";
import { Step1 } from "./Step1";
import { Step2 } from "./Step2";

import { useDialogReset } from "../../../../../../hooks/useDialogs";
import { layers } from "../../../../../../resources/layers";

import { CopyToExistingProps, LayerCopyField } from "../types";

export const CopyToExisting: FC<CopyToExistingProps> = ({
  layerInfo,
  shadows,
  setScrollContainer,
  currentStep,
  setCurrentStep,
  projectLayersLoading,
  allLayersLoading,
  filteredLayers,
}) => {
  const { t } = useTranslation("common");
  const cancelDialog = useDialogReset();
  const [targetLayer, setTargetLayer] = useState<ServiceInfoDc>();
  const [copyFields, setCopyFields] = useState<LayerCopyField[]>([]);
  const [isCopying, setCopying] = useState<boolean>(false);

  const fieldsToCopy = useMemo(
    () => copyFields.filter(({ targetField, enabled }) => !!targetField && enabled),
    [copyFields],
  );

  return (
    <>
      <DialogContent
        ref={setScrollContainer}
        style={{
          width: "100%",
          justifyContent: "center",
          padding: 0,
          flexGrow: 0,
        }}
      >
        {currentStep === 0 && (
          <Step0
            layerInfo={layerInfo}
            onTargetChange={setTargetLayer}
            projectLayersLoading={projectLayersLoading}
            allLayersLoading={allLayersLoading}
            filteredLayers={filteredLayers}
          />
        )}
        {currentStep === 1 && <Step1 layerInfo={layerInfo} targetLayer={targetLayer} setCopyFields={setCopyFields} />}
        {currentStep === 2 && (
          <Step2
            layerInfo={layerInfo}
            targetLayer={targetLayer}
            copyFields={fieldsToCopy}
            onTaskStatusChange={setCopying}
          />
        )}
      </DialogContent>
      <DialogActionsContainer showShadow={shadows.bottom}>
        <RestrictedWidthContainer>
          <ActionsGroup style={{ width: "100%", padding: "2rem 0" }}>
            <ApplyButton
              isWaiting={currentStep === 2 && isCopying}
              primary
              onClick={() => {
                if (currentStep < 2) {
                  setCurrentStep(currentStep + 1);
                }

                if (currentStep === 2) {
                  layers.invalidateAll();
                  cancelDialog();
                }
              }}
              disabled={
                (currentStep === 0 && !targetLayer) ||
                (currentStep === 1 && !fieldsToCopy.length) ||
                (currentStep === 2 && isCopying)
              }
            >
              {currentStep === 0 && t("actions.next")}
              {currentStep === 1 && t("actions.copy")}
              {currentStep === 2 && t("actions.ready")}
            </ApplyButton>
            {currentStep === 1 && (
              <FlatButton primary onClick={() => setCurrentStep(0)}>
                {t("actions.back")}
              </FlatButton>
            )}
            <CancelButton onClick={cancelDialog}>{t("actions.cancel")}</CancelButton>
          </ActionsGroup>
        </RestrictedWidthContainer>
      </DialogActionsContainer>
    </>
  );
};
